/* eslint-disable no-param-reassign */
import expect from '@src/slide/infrastructure/module/independent/assert/expect';
import * as mobx from 'mobx';

/**
 * 展开path，a -> a, a[0] -> ['a', '0'], a['['] -> ['a', '\'[\'']
 * // TODO a[b][c] -> ['a', 'b', 'c']
 * @param {str} path
 * @return {array|string} 转换后的path
 */
function convertArray(path) {
    // eslint-disable-next-line no-useless-escape
    const match = path.match(/^([^\[]+)\[(.+)\]$/);
    if (match) {
        return [match[1], match[2]];
    }
    return path;
}

/**
 * 深层遍历展开arr
 * [a, [b], [[c, d], e]] -> [a, b, c, d, e]
 * @param {*} arr
 * @return {array} 展开后的数组
 */
function arrExpansion(arr: Array<any>) {
    const a: any = [];
    arr.forEach((item) => {
        if (expect(item).isArray()) {
            a.push(...arrExpansion(item));
        } else {
            a.push(item);
        }
    });
    return a;
}

function convert(path) {
    return arrExpansion(path.split('.').map(item => convertArray(item)));
}

/**
 * 判断obj是否包含path，同lodash，支持observerableArray
 * @param {object} obj
 * @param {string|array} path
 */
export function has(obj, path) {
    const p = expect(path).isArray() ? path : convert(path);
    const { length } = p;
    let o = obj;
    // 最后一个值可以是undefined，比如 has({ a: { b: undefined } }, 'a.b') 返回true
    // 但是之前的每个必须是array或者object，所以不比较最后一个
    for (let i = 0; i < length - 1; i++) {
        o = o[p[i]];
        if (expect(o).isBasic()) return false;
    }

    const last = p[length - 1];
    if (isNaN(Number(last))) {
        // 最后一项不是数组值
        return last in o;
    }
    return !expect(o[last]).isUndefined();
}

/** *
 *
 * @param obj           JS or Mobx Object
 * @param path          keyPath
 * @returns {boolean}   是否存在
 */
export function advHas(obj, path) {
    const p = expect(path).isArray() ? path : convert(path);
    const { length } = p;

    if (length < 1) throw new TypeError('advHas error');

    let o = obj;
    for (let i = 0; i < length; i++) {
        const key = p[i];
        if (mobx.isObservable(o) && mobx.has(o, key)) {
            o = mobx.get(o, key);
        // eslint-disable-next-line no-undef
        } else if (typeof o?.[key] !== 'undefined') {
            o = o[key];
        } else {
            return false;
        }
    }

    return true;
}


/**
 * 判断obj中是否存在某个属性
 * @param {object} obj  JS 对象 or 有原型对象
 * @param {string} prop 属性名称
 * @param {string} gs   'get' or 'set'
 * @returns {boolean}   是否存在
 */
export function hasProperty(obj, prop, gs) {
    let desc;
    let o = obj;
    do {
        desc = Object.getOwnPropertyDescriptor(o, prop);
    } while (!desc && (o = Object.getPrototypeOf(o)));

    if (typeof desc === 'undefined') {
        return false;
    }
    if (gs !== undefined) {
        return typeof desc[gs] !== 'undefined';
    }
    return true;
}

export function mobxSet(obj, path, value) {
    const keys = path.split('.');
    for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        if (obj[key] === undefined || obj[key] === null) {
            mobx.set(obj, key, {});
        }
        obj = obj[key];
    }
    mobx.set(obj, keys[keys.length - 1], value);
}

export function mobxRemove(obj, path) {
    const keys = path.split('.');
    for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        if (obj[key] === undefined) {
            return;
        }
        obj = obj[key];
    }
    mobx.remove(obj, keys[keys.length - 1]);
}

/**
 * mbox对象将get与set转换成了!enumerable属性，使用...无法展开，需要先用本方法转换
 * 同时去掉_与$前缀的私有变量
 * @param {object} obj
 */
export function omitStyle(obj) {
    const o = {};
    Object.getOwnPropertyNames(obj).forEach((key) => {
        if (!/^(_|\$).*/.test(key)) o[key] = obj[key];
    });
    return o;
}
